import { useMemo } from 'react';

import { Alert, Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { CHART_CARD_SX } from './chartCardSx';
import { useFollowingViewContext } from '../../../Wallet/FollowingView/FollowingViewContext';
import { formatConcentrationPercentage } from '../concentration/formatters';
import { getFicFidcCockpitConcentrationFeedback } from '../concentration/statusMessages';
import { useFicFidcCockpitConcentration } from '../concentration/useFicFidcCockpitConcentration';

import type { FicFidcCockpitConcentrationCurvePoint } from '../concentration/mapFicFidcCockpitConcentration';

/**
 * Card "Concentração (por fundo)": linha/área acumulada dos percentuais
 * reportados pelo backend por posição do FIC, alinhada à referência visual
 * UQ-4504 (SVG compacto com gradiente, pontos e resumo de composição).
 */

const CHART_ID = 'fic-fidc-fund-concentration-chart';
const CURVE_TESTID = 'fic-fidc-fund-concentration-chart-curve';
const SUMMARY_TESTID = 'fic-fidc-fund-concentration-chart-summary';
const GRADIENT_ID = 'fic-fidc-fund-concentration-fill';

/** Geometria do SVG compacto (viewBox 680x220) da referência visual. */
const VIEWBOX_WIDTH = 680;
const VIEWBOX_HEIGHT = 220;
const PLOT_LEFT = 46;
const PLOT_RIGHT = 672;
const PLOT_BOTTOM = 192;
const PLOT_TOP = 36;

function getPointX(index: number, total: number) {
  if (total <= 1) return PLOT_LEFT;

  return PLOT_LEFT + (index * (PLOT_RIGHT - PLOT_LEFT)) / (total - 1);
}

function getPointY(
  cumulativePercentage: number,
  plotTop = PLOT_TOP,
  plotBottom = PLOT_BOTTOM,
) {
  const clampedPercentage = Math.min(100, Math.max(0, cumulativePercentage));

  return plotBottom - (clampedPercentage / 100) * (plotBottom - plotTop);
}

interface PlottedCurvePoint extends FicFidcCockpitConcentrationCurvePoint {
  x: number;
  y: number;
}

interface FundConcentrationCurveProps {
  points: FicFidcCockpitConcentrationCurvePoint[];
  height?: number;
  variant?: 'compact' | 'complete';
  gradientId?: string;
  testId?: string;
}

export function FundConcentrationCurve({
  points,
  height = 128,
  variant = 'compact',
  gradientId = GRADIENT_ID,
  testId = CURVE_TESTID,
}: FundConcentrationCurveProps) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompleteMobile = variant === 'complete' && isNarrow;
  const primary = theme.palette.primary.main;
  const viewBoxHeight = isCompleteMobile ? 640 : VIEWBOX_HEIGHT;
  const plotTop = isCompleteMobile ? 84 : PLOT_TOP;
  const plotBottom = isCompleteMobile ? 594 : PLOT_BOTTOM;
  const labelFontSize =
    variant === 'complete' ? (isCompleteMobile ? 24 : 12) : 18;
  const firstPointRadius = isCompleteMobile ? 7 : 3.5;
  const lastPointRadius = isCompleteMobile ? 9 : 4.5;

  const plotted = useMemo<PlottedCurvePoint[]>(
    () =>
      points.map((point, index) => ({
        ...point,
        x: getPointX(index, points.length),
        y: getPointY(point.cumulativePercentage, plotTop, plotBottom),
      })),
    [plotBottom, plotTop, points],
  );

  const first = plotted[0] ?? null;
  const last = plotted[plotted.length - 1] ?? null;

  if (!first || !last) return null;

  const hasLine = plotted.length >= 2;
  const linePoints = plotted.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPath = hasLine
    ? [
        `M ${first.x} ${plotBottom}`,
        ...plotted.map((point) => `L ${point.x} ${point.y}`),
        `L ${last.x} ${plotBottom}`,
        'Z',
      ].join(' ')
    : null;
  const lastLabel = last !== first ? last : null;

  return (
    <svg
      aria-label="Concentração acumulada dos fundos que compõem o FIC"
      data-testid={testId}
      role="img"
      style={{
        display: 'block',
        height,
        overflow: 'visible',
        width: '100%',
      }}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${viewBoxHeight}`}>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={primary} stopOpacity={0.2} />
          <stop offset="1" stopColor={primary} stopOpacity={0} />
        </linearGradient>
      </defs>

      {areaPath && (
        <path d={areaPath} fill={`url(#${gradientId})`} opacity={0.9} />
      )}

      {hasLine && (
        <polyline
          fill="none"
          points={linePoints}
          stroke={primary}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.7}
        />
      )}

      <circle
        cx={first.x}
        cy={first.y}
        fill={theme.palette.background.paper}
        r={firstPointRadius}
        stroke={primary}
        strokeWidth={2}
      />
      {lastLabel && (
        <circle
          cx={lastLabel.x}
          cy={lastLabel.y}
          fill={theme.palette.background.paper}
          r={lastPointRadius}
          stroke={primary}
          strokeWidth={2}
        />
      )}

      <text
        aria-hidden
        fill={theme.palette.text.secondary}
        fontSize={labelFontSize}
        fontWeight={700}
        textAnchor="start"
        x={first.x}
        y={first.y - 7}>
        {formatConcentrationPercentage(first.cumulativePercentage, 1)}
      </text>
      {lastLabel && (
        <text
          aria-hidden
          fill={theme.palette.text.secondary}
          fontSize={labelFontSize}
          fontWeight={700}
          textAnchor="end"
          x={lastLabel.x}
          y={lastLabel.y - 10}>
          {formatConcentrationPercentage(lastLabel.cumulativePercentage, 1)}
        </text>
      )}
    </svg>
  );
}

function FundConcentrationSummary({
  points,
}: {
  points: FicFidcCockpitConcentrationCurvePoint[];
}) {
  const first = points[0];
  const last = points[points.length - 1];

  if (!first || !last) return null;

  const compositionLabel = `${points.length} ${
    points.length === 1 ? 'fundo' : 'fundos'
  } na composição`;

  return (
    <Typography
      color="text.secondary"
      data-testid={SUMMARY_TESTID}
      sx={{ display: 'block', mt: 1 }}
      variant="caption">
      {`${formatConcentrationPercentage(
        first.cumulativePercentage,
        1,
      )} → ${formatConcentrationPercentage(
        last.cumulativePercentage,
        1,
      )} · ${compositionLabel}`}
    </Typography>
  );
}

function FundConcentrationSkeleton() {
  return (
    <Box
      aria-label="Carregando gráfico de concentração por fundo"
      data-testid="fic-fidc-fund-concentration-chart-loading"
      sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
      <Skeleton height={16} width="42%" />
      <Skeleton sx={{ flex: 1, minHeight: 0, mt: 1 }} variant="rectangular" />
    </Box>
  );
}

export function FicFidcFundConcentrationChart() {
  const { operationId, indicatorsCompetencyDate } = useFollowingViewContext();
  const { status, data } = useFicFidcCockpitConcentration(
    operationId,
    indicatorsCompetencyDate,
  );
  const feedback = getFicFidcCockpitConcentrationFeedback(
    status,
    data?.reason ?? null,
  );

  const curve =
    status === 'ready' || status === 'partial' ? data?.curve ?? [] : [];

  return (
    <Box
      id={CHART_ID}
      aria-busy={status === 'loading'}
      component="article"
      data-state={status}
      aria-label="Gráfico de concentração por fundo"
      sx={(theme) => ({
        ...CHART_CARD_SX,
        p: { xs: 1.5, sm: 2 },
        boxSizing: 'border-box',
      })}>
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          mb: 1.5,
          flexShrink: 0,
        }}>
        <Typography component="h3" fontWeight={700} variant="subtitle1">
          Concentração (por fundo)
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ flexShrink: 0 }}
          variant="caption">
          Fundos do FIC
        </Typography>
      </Box>

      {feedback && (
        <Alert
          data-testid="fic-fidc-fund-concentration-chart-feedback"
          role="status"
          severity={feedback.severity}
          variant="outlined">
          {feedback.message}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flex: '0 0 auto',
          flexDirection: 'column',
          minHeight: 0,
        }}>
        {status === 'loading' || !data ? (
          <FundConcentrationSkeleton />
        ) : status === 'ready' || status === 'partial' ? (
          <>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '0 0 auto',
                minHeight: 0,
              }}>
              {curve.length > 0 && <FundConcentrationCurve points={curve} />}
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <FundConcentrationSummary points={curve} />
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

export default FicFidcFundConcentrationChart;
